import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { Cross2Icon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { useAuth } from '../../contexts/AuthContext';
import { getQuotaStatus } from '../../services/api';
import config from '../../config/config';

const documentTypes = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'vat_book', label: 'VAT Book' },
  { value: 'cashbook', label: 'CashBook' },
];

interface UploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadSuccess?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ open, onOpenChange, onUploadSuccess }) => {
  const { currentUser } = useAuth();
  const [selectedType, setSelectedType] = useState('');
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [quotaStatus, setQuotaStatus] = useState<any>(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);

  // Load quota status when modal opens
  useEffect(() => {
    if (open) {
      loadQuotaStatus();
    }
  }, [open]);

  const loadQuotaStatus = async () => {
    setIsLoadingQuota(true);
    try {
      const status = await getQuotaStatus();
      setQuotaStatus(status);
    } catch (error) {
      console.error('Failed to load quota status:', error);
      setQuotaStatus(null);
    } finally {
      setIsLoadingQuota(false);
    }
  };

  const handleFile = (file: File) => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setUploadError('File size should not exceed 100MB');
      return;
    }
    
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Only PDF, PNG, and JPG files are allowed');
      return;
    }
    
    setFile(file);
    setFileName(file.name);
    setUploadError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedType) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', selectedType);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${config.apiBaseUrl}/documents/`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload document');
      }
      
      const result = await response.json();
      console.log('Upload successful:', result);
      
      // Reset form and close modal
      onOpenChange(false);
      setFile(null);
      setFileName('');
      setSelectedType('');
      
      // Refresh quota status after successful upload
      await loadQuotaStatus();
      
      // Notify parent component about successful upload
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 dark:bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-2xl">
          <Dialog.Title className="text-xl font-semibold mb-4 dark:text-gray-100">Upload Document</Dialog.Title>
          <Dialog.Description className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Upload a document and select its type.
          </Dialog.Description>
          
          {/* Quota Status Display */}
          {!isLoadingQuota && quotaStatus && (
            <div className={`mb-4 p-3 rounded-md border ${
              quotaStatus.quota_exceeded 
                ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800' 
                : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${
                    quotaStatus.quota_exceeded 
                      ? 'text-red-800 dark:text-red-200' 
                      : 'text-blue-800 dark:text-blue-200'
                  }`}>
                    {quotaStatus.quota_exceeded ? 'Quota Exceeded' : 'Quota Status'}
                  </p>
                  <p className={`text-xs ${
                    quotaStatus.quota_exceeded 
                      ? 'text-red-600 dark:text-red-300' 
                      : 'text-blue-600 dark:text-blue-300'
                  }`}>
                    {quotaStatus.pages_used} / {quotaStatus.pages_quota} pages used ({quotaStatus.usage_percentage}%)
                  </p>
                </div>
                {quotaStatus.quota_exceeded && (
                  <div className="text-red-600 dark:text-red-400">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Type</label>
              <Select.Root value={selectedType} onValueChange={setSelectedType}>
                <Select.Trigger 
                  className="inline-flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  aria-label="Document Type"
                >
                  <Select.Value placeholder="Select document type" />
                  <Select.Icon>
                    <ChevronDownIcon className="text-gray-500 dark:text-gray-400" />
                  </Select.Icon>
                </Select.Trigger>
                
                <Select.Portal>
                  <Select.Content className="overflow-hidden bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
                    <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-default">
                      <ChevronUpIcon />
                    </Select.ScrollUpButton>
                    
                    <Select.Viewport className="p-1">
                      {documentTypes.map((type) => (
                        <Select.Item
                          key={type.value}
                          value={type.value}
                          className="relative flex items-center px-8 py-2 text-sm text-gray-700 dark:text-gray-200 rounded-md select-none hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none"
                        >
                          <Select.ItemText>{type.label}</Select.ItemText>
                          <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                            <CheckIcon />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                    
                    <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-default">
                      <ChevronDownIcon />
                    </Select.ScrollDownButton>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File</label>
              <div className="flex items-center justify-center w-full">
                <label 
                  className={`flex flex-col w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
                    isDragging 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}>
                  <div className="flex flex-col items-center justify-center pt-8 pb-8">
                    {fileName ? (
                      <>
                        <svg className="w-10 h-10 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-base text-gray-700 dark:text-gray-300 font-medium">{fileName}</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Click to change file</p>
                      </>
                    ) : (
                      <>
                        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="mt-3 text-base text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-primary">Click to upload</span> or drag and drop
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">PDF, PNG, JPG (MAX. 100MB)</p>
                      </>
                    )}
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept=".pdf,.png,.jpg,.jpeg" 
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>
          </div>
          
          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
            </div>
          )}
          
          <div className="mt-8 flex justify-end space-x-4">
            <Dialog.Close asChild>
              <button 
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary"
                disabled={isUploading}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button 
              className={`px-5 py-2.5 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                quotaStatus?.quota_exceeded 
                  ? 'text-gray-400 bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
                  : 'text-white bg-primary hover:bg-blue-600'
              }`}
              onClick={handleUpload}
              disabled={!selectedType || !file || isUploading || quotaStatus?.quota_exceeded}
              title={quotaStatus?.quota_exceeded ? `Quota exceeded. You have used ${quotaStatus.pages_used}/${quotaStatus.pages_quota} pages.` : ''}
            >
              {isUploading ? 'Uploading...' : quotaStatus?.quota_exceeded ? 'Quota Exceeded' : 'Upload'}
            </button>
          </div>
          
          <Dialog.Close asChild>
            <button 
              className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UploadModal;
